import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, MapPin, Tag, ArrowLeft, MessageCircle } from "lucide-react";
import api, { imgUrl } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { useSettings } from "@/context/SettingsContext";

export function EventsList() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/events").then(({ data }) => setEvents(data)); }, []);

  const filtered = filter === "all" ? events : events.filter((e) => e.event_type === filter);
  const types = ["all", ...Array.from(new Set(events.map((e) => e.event_type)))];

  return (
    <div data-testid="events-page" className="bg-[var(--pz-ink)] text-white">
      <SEOHead title="Events — The Arena" description="Premier League, corporate tournaments and private bookings at The Arena." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
        <div className="text-xs uppercase tracking-[0.35em] text-[var(--pz-red)] font-bold mb-4">What's on</div>
        <h1 className="font-display text-5xl md:text-7xl font-bold uppercase leading-none">Events</h1>
        <p className="mt-6 text-zinc-400 max-w-2xl">Browse upcoming tournaments, corporate leagues and floodlit finals. Tap any event to register or enquire on WhatsApp.</p>

        <div className="mt-10 flex flex-wrap gap-2">
          {types.map((t) => (
            <button
              key={t}
              data-testid={`event-filter-${t}`}
              onClick={() => setFilter(t)}
              className={`px-5 py-2 rounded-full text-xs uppercase tracking-[0.15em] font-bold capitalize transition ${filter === t ? "bg-[var(--pz-red)] text-white" : "border border-[var(--pz-line)] text-zinc-300 hover:border-[var(--pz-red)]"}`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {filtered.map((e, i) => (
            <Link
              to={`/events/${e.slug}`}
              key={e.id}
              data-testid={`event-card-${i}`}
              className="bg-[var(--pz-ink-2)] border border-[var(--pz-line)] rounded-md overflow-hidden hover:-translate-y-1 transition-all group pz-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              {e.image_url && (
                <div className="relative overflow-hidden">
                  <img src={imgUrl(e.image_url)} alt={e.title} className="w-full h-52 object-cover transition duration-700 group-hover:scale-105" />
                  <span className="absolute top-3 left-3 bg-[var(--pz-red)] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5">{e.event_type}</span>
                  {e.is_featured && <span className="absolute top-3 right-3 bg-yellow-400 text-zinc-900 text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5">Featured</span>}
                </div>
              )}
              <div className="p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{new Date(e.start_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
                <h3 className="font-display text-xl font-bold uppercase mt-2 text-white group-hover:text-[var(--pz-red)] transition">{e.title}</h3>
                <p className="text-zinc-400 text-sm mt-2 line-clamp-2">{e.description}</p>
                <div className="mt-4 flex flex-wrap gap-3 text-xs text-zinc-500">
                  {e.venue && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.venue}</span>}
                </div>
                {e.price && <div className="mt-3 text-[var(--pz-red)] font-bold text-sm">{e.price}</div>}
              </div>
            </Link>
          ))}
          {filtered.length === 0 && <div className="col-span-full text-zinc-500">No events scheduled yet — check back soon.</div>}
        </div>
      </div>
    </div>
  );
}

export function EventDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const { settings } = useSettings();

  useEffect(() => {
    api.get(`/events/${slug}`).then(({ data }) => setEvent(data)).catch(() => setEvent(false));
  }, [slug]);

  if (event === null) return <div className="bg-[var(--pz-ink)] py-32 text-center text-zinc-500">Loading…</div>;
  if (event === false) return <div className="bg-[var(--pz-ink)] py-32 text-center text-zinc-500">Event not found. <Link to="/events" className="text-[var(--pz-red)] font-bold">See all events</Link></div>;

  const wa = `https://wa.me/${(settings.whatsapp_number || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi, I'd like to register for "${event.title}"`)}`;

  return (
    <div data-testid="event-detail-page" className="bg-[var(--pz-ink)] text-white">
      <SEOHead title={`${event.title} — The Arena`} description={event.description} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/events" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-xs uppercase tracking-[0.2em] font-bold mb-8"><ArrowLeft className="h-3.5 w-3.5" /> Back to events</Link>
        {event.image_url && <img src={imgUrl(event.image_url)} alt={event.title} className="w-full aspect-[16/9] object-cover rounded-md" />}
        <div className="mt-10">
          <span className="bg-[var(--pz-red)] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 inline-block">{event.event_type}</span>
          <h1 className="font-display text-4xl md:text-6xl font-bold uppercase mt-5 leading-none">{event.title}</h1>
          <div className="mt-6 flex flex-wrap gap-5 text-zinc-400 text-sm">
            <span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-[var(--pz-red)]" /> {new Date(event.start_date).toLocaleString()}</span>
            {event.venue && <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[var(--pz-red)]" /> {event.venue}</span>}
            {event.price && <span className="flex items-center gap-2"><Tag className="h-4 w-4 text-[var(--pz-red)]" /> {event.price}</span>}
          </div>
          <div className="prose-pz mt-10">
            <p className="whitespace-pre-line">{event.description}</p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href={wa} target="_blank" rel="noreferrer" data-testid="event-register-whatsapp" className="btn-red"><MessageCircle className="h-4 w-4" /> Register on WhatsApp</a>
            <Link to="/contact" className="btn-outline">Send Enquiry</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
